import { sendLabOrderV2 } from '../../../services/labIntegrationService';

// Simple HL7 builder for demonstration
export function buildHL7FromProcedure(proc: any): string {
  // This is a placeholder. Real HL7 will be more complex.
  return `MSH|^~\\&|EMR|${proc.facility}|LAB|||${new Date().toISOString()}||ORM^O01|${proc.id || ''}|P|2.3\nPID|||${proc.patientId}||\nORC|NW|${proc.id || ''}||\nOBR|||${proc.testCode}^${proc.testType}||${proc.orderDate?.toISOString?.() || ''}`;
}

/**
 * Send order via lab integration router (supports SFTP/API and HL7/FHIR)
 * Uses the lab integration's configuration to determine protocol and format
 * 
 * @param procedure - The procedure/order data
 * @param labIntegrationId - The lab integration ID to use
 * @param patient - Patient demographics for HL7/FHIR message building
 * @returns Result with success status, protocol used, and data format
 */
export async function sendOrderViaIntegration(
  procedure: any, 
  labIntegrationId: string,
  patient?: {
    id: string;
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    gender?: string;
    mrn?: string;
    ssn?: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      zip?: string;
    };
    phone?: string;
    email?: string;
  }
): Promise<{ 
  success: boolean; 
  error?: string;
  protocol?: string;
  dataFormat?: string;
  messageId?: string;
}> {
  try {
    if (!labIntegrationId) {
      return {
        success: false,
        error: 'No lab integration selected. Please select a lab to send the order to.'
      };
    }

    // Build order data with patient demographics for HL7/FHIR message building
    const orderData = {
      id: procedure.id,
      patientId: procedure.patientId,
      orderDate: procedure.orderDate instanceof Date 
        ? procedure.orderDate.toISOString() 
        : procedure.orderDate,
      testType: procedure.testType,
      testCode: procedure.testCode,
      notes: procedure.notes,
      priority: procedure.priority || 'Routine',
      orderingProviderName: procedure.orderingProvider,
      orderingFacility: procedure.orderingFacility,
      // Lab-specific fields
      specimenType: procedure.specimenType,
      fastingRequired: procedure.fastingRequired,
      collectionTime: procedure.collectionTime,
      // Imaging-specific fields
      modality: procedure.testType === 'Imaging' ? procedure.testCode : undefined,
      bodySite: procedure.bodySite,
      laterality: procedure.laterality,
      // Patient demographics for HL7 PID segment / FHIR Patient resource
      patientFirstName: patient?.firstName,
      patientLastName: patient?.lastName,
      patientDateOfBirth: patient?.dateOfBirth,
      patientGender: patient?.gender,
      patientMRN: patient?.mrn || patient?.id,
      patientSSN: patient?.ssn,
      patientAddress: patient?.address,
      patientPhone: patient?.phone,
      patientEmail: patient?.email,
    };

    // Call the lab integration service which uses the backend integration router
    const result = await sendLabOrderV2(labIntegrationId, orderData) as {
      success?: boolean;
      protocol?: string;
      dataFormat?: string;
      messageId?: string;
      orderId?: string;
      error?: string;
    };
    
    return {
      success: result.success !== false,
      protocol: result.protocol,
      dataFormat: result.dataFormat,
      messageId: result.messageId || result.orderId,
      error: result.error
    };
  } catch (error: any) {
    console.error('Error sending order via integration:', error);
    return { 
      success: false, 
      error: error.response?.data?.error || error.message || 'Failed to send order via lab integration'
    };
  }
}

/**
 * Legacy function - Send HL7 order to lab/imaging center
 * @deprecated Use sendOrderViaIntegration instead for proper protocol/format support
 */
export async function sendHL7Order(procedure: any, idToken: string): Promise<{ success: boolean; error?: string }> {
  try {
    const remotePath = `/emr/${procedure.id || Date.now()}.hl7`;
    const resp = await fetch('https://api-harbzwofua-uc.a.run.app/internal/send-hl7-order', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ procedure, remotePath }),
    });
    
    const result = await resp.json();
    return result;
  } catch (error: any) {
    return { 
      success: false, 
      error: error.message || 'Unknown error occurred' 
    };
  }
} 